import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { neutralHoverSx } from './iconButtonStyles';

import SettingsIcon from '@mui/icons-material/Settings';
import TurnedInIcon from '@mui/icons-material/TurnedIn';
import PhotoIcon from '@mui/icons-material/Photo';
import PestControlIcon from '@mui/icons-material/PestControl';
import CheckIcon from '@mui/icons-material/Check';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';

interface ViewOptionsMenuProps {
  imageToggle: boolean;
  labelToggle: boolean;
  debugToggle: boolean;
  onImageToggle: () => void;
  onLabelToggle: () => void;
  onDebugToggle: () => void;
  onCollapseAll?: () => void;
  showCollapseAll?: boolean;
}

export function ViewOptionsMenu({
  imageToggle,
  labelToggle,
  debugToggle,
  onImageToggle,
  onLabelToggle,
  onDebugToggle,
  onCollapseAll,
  showCollapseAll = false,
}: ViewOptionsMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action: () => void) => {
    action();
    // Don't close menu - let users toggle multiple options
  };

  return (
    <>
      <Tooltip title="View options">
        <IconButton
          color="inherit"
          onClick={handleClick}
          sx={{
            ...neutralHoverSx,
            mr: 2,
          }}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: '8px',
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 2px 8px rgba(0, 0, 0, 0.5)'
              : '0 2px 8px rgba(0, 0, 0, 0.15)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => handleMenuItemClick(onImageToggle)}>
          <ListItemIcon>
            <PhotoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Show pictures"
            primaryTypographyProps={{ fontSize: 14 }}
          />
          {imageToggle && (
            <CheckIcon fontSize="small" sx={{ ml: 2, color: 'primary.main' }} />
          )}
        </MenuItem>

        <MenuItem onClick={() => handleMenuItemClick(onLabelToggle)}>
          <ListItemIcon>
            <TurnedInIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Show labels"
            primaryTypographyProps={{ fontSize: 14 }}
          />
          {labelToggle && (
            <CheckIcon fontSize="small" sx={{ ml: 2, color: 'primary.main' }} />
          )}
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => handleMenuItemClick(onDebugToggle)}>
          <ListItemIcon>
            <PestControlIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Debug mode"
            primaryTypographyProps={{ fontSize: 14 }}
          />
          {!debugToggle && (
            <CheckIcon fontSize="small" sx={{ ml: 2, color: 'primary.main' }} />
          )}
        </MenuItem>

        {showCollapseAll && onCollapseAll && [
          <Divider key="divider" />,
          <MenuItem key="collapse" onClick={() => {
            onCollapseAll();
            handleClose();
          }}>
            <ListItemIcon>
              <FolderOpenIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Collapse all sections"
              primaryTypographyProps={{ fontSize: 14 }}
            />
          </MenuItem>
        ]}
      </Menu>
    </>
  );
}
